import React from 'react';

type Member = {
  id?: number | string;
  name: string;
  team: string;
};

type MultiAssigneePickerProps = {
  label?: string;
  members: Member[];
  value: string[];
  onChange: (value: string[]) => void;
};

export function MultiAssigneePicker({
  label = 'Assigned To',
  members,
  value,
  onChange,
}: MultiAssigneePickerProps) {
  const selected = React.useMemo(
    () => new Set(value.map(name => name.toLowerCase())),
    [value]
  );

  const toggle = (name: string) => {
    if (selected.has(name.toLowerCase())) {
      onChange(value.filter(current => current.toLowerCase() !== name.toLowerCase()));
      return;
    }
    onChange([...value, name]);
  };

  return (
    <div className="w-full">
      <div className="flex items-center justify-between gap-3 mb-1.5">
        <label className="block text-sm font-semibold text-[#18181B]">{label}</label>
        {value.length > 0 && (
          <button
            type="button"
            onClick={() => onChange([])}
            className="text-xs font-medium text-[#71717A] hover:text-[#DC2626]"
          >
            Clear
          </button>
        )}
      </div>
      <div className="rounded-lg border border-[#E4E4E7] bg-white p-2 max-h-40 overflow-y-auto">
        {members.length === 0 ? (
          <p className="px-2 py-2 text-sm text-[#A1A1AA]">No members found</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
            {members.map(member => {
              const checked = selected.has(member.name.toLowerCase());
              return (
                <label
                  key={member.id || member.name}
                  className={`flex items-center gap-2 rounded-md px-2 py-1.5 text-sm cursor-pointer ${
                    checked ? 'bg-orange-50 text-orange-700' : 'text-[#3F3F46] hover:bg-[#F4F4F5]'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggle(member.name)}
                    className="h-4 w-4 rounded border-[#D4D4D8] text-[#F97316] focus:ring-[#F97316]"
                  />
                  <span className="min-w-0">
                    <span className="font-medium">{member.name}</span>
                    <span className="ml-1 text-xs text-[#71717A]">({member.team})</span>
                  </span>
                </label>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

type FollowUpOwnerSelectProps = {
  members: Member[];
  value: string;
  onChange: (value: string) => void;
};

export function FollowUpOwnerSelect({ members, value, onChange }: FollowUpOwnerSelectProps) {
  const seoMembers = members.filter(member => member.team === 'SEO');
  const options = seoMembers.length > 0 ? seoMembers : members;

  return (
    <div className="w-full">
      <label className="block text-sm font-semibold text-[#18181B] mb-1.5">Follow-up Owner</label>
      <select
        value={value}
        onChange={event => onChange(event.target.value)}
        className="w-full rounded-lg border border-[#E4E4E7] bg-white px-3 py-2 text-sm text-[#18181B] focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-[#F97316] transition-all duration-150"
      >
        <option value="">No follow-up owner</option>
        {options.map(member => (
          <option key={member.id || member.name} value={member.name}>
            {member.name} ({member.team})
          </option>
        ))}
      </select>
    </div>
  );
}
